import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { config } from '@uhg-abyss/web/tools/config';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { storage } from '@uhg-abyss/web/tools/storage';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { adobeFuncErrorEvent } from '../../common/AdobeTagging/adobeFuncErrorEvent';
import { Constants, ProviderTypes } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { PortalIdentifier } from '../../common/Portal';
import { PSXHeader } from '../../common/PSXHeader';
import { getGeoLocationFromStorage } from '../../common/PSXHeader/SearchBar/utils';
import { StillNeedHelp } from '../../common/StillNeedHelp';
import { getFeatureFlag } from '../../common/Utils';
import { PortalContext } from '../../context/PortalContext';
import {
  concatenateStrings,
  getTelemetryCoverageType,
} from '../../frontends/ProviderSearch/routes/MapView/utils';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useLagoon } from '../../hooks/useLagoon';
import { useProviderSearchTelemetry } from '../../hooks/useProviderSearchTelemetry';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import { isGuestFlow } from '../../utils/error.utils';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { getCurrentMember } from '../../utils/user.utils';
import { PageContainer, PageContent } from './Page4XX.styled';

export const WHISTLE_PNG = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/whistle.png`;

export const Page4XX = () => {
  const { t } = useTranslation();
  const breadcrumbs = [{ title: t('ERROR_PAGE.4xx.title'), href: '' }];
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    is404: true,
    pageName: 'error 4xx',
  });

  const currentMember = getCurrentMember();
  const { getRouteParams } = useRouter();
  const { token } = getRouteParams();
  const tokenData = tokenizer.parse(token) || {};
  const { message, code, source, uiSection } = tokenData;
  const isGuest = isGuestFlow();

  const { portalData } = useContext(PortalContext);
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const suppressPsxLinks: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PSX_LINKS_ON_MEMBER_INFO_FAILURE
  );

  const [searchParams] = useSearchParams();
  const enableAutoCompleteTelemetry = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RELEVANCY_EVENTS_DASHBOARD
  );
  useEffect(() => {
    adobeFuncErrorEvent({
      message,
      source,
      code: code ?? '4xx',
      uiSection,
    });
    adobePageTrackEvent();
  }, []);

  const { coverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
    }))
  );

  const [correlationIDSessionStorage] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.CORRELATION_ID_HEADER,
    ''
  );

  const [, providerSearchTelemetry] = useProviderSearchTelemetry({});
  const { longitude, latitude, name } = getGeoLocationFromStorage();
  const { searchRadius } = useSearchStore(
    useShallow((state: SearchState) => ({
      searchRadius: state.searchStore.searchRadius,
    }))
  );
  const { searchTerm, searchType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      searchTerm: state.typeaheadSearchStore.searchTerm,
      searchFromTypeAhead: state.typeaheadSearchStore.searchFromTypeAhead,
      searchBySpeciality: state.typeaheadSearchStore.searchBySpeciality,
      searchType: state.typeaheadSearchStore.searchType,
    }))
  );

  const hitProviderSearchTelemetry = async () => {
    const providerList = storage.session.get(
      Constants.STORAGE_KEYS.SESSION.PROVIDER_LIST
    );
    const filteredResults = providerList
      .map((item) => {
        if (!item) return null; // Skip invalid items

        const providerType = item?.providerType?.toLowerCase();
        const address = item?.displayAddress
          ? item?.displayAddress
          : concatenateStrings(
              item?.address?.line?.join('') || '',
              item?.address?.city || '',
              item?.address?.district || '',
              item?.address?.state || '',
              item?.address?.postalCode || ''
            );

        return {
          resultId: item?.providerId || '',
          resultType:
            providerType === ProviderTypes?.PRACTITIONER
              ? 'PROVIDER'
              : providerType === ProviderTypes.MEDICAL_GROUP
              ? 'GROUP'
              : 'FACILITY',
          name: item?.providerName || item?.displayName || '',
          specialty: item?.speciality?.[0]?.category || item?.speciality || '',
          address,
          coverageType: Array.isArray(coverageType)
            ? coverageType?.map(getTelemetryCoverageType)
            : getTelemetryCoverageType(coverageType),
          rating: item?.healthGradeRating
            ? parseInt(item?.healthGradeRating)
            : 0,
          numRatings: item?.surveyCount || 0,
          inNetworkLabel: item?.networkStatus || '',
          costIndicationLabel: item?.costIndicator || '',
        };
      })
      .filter(Boolean);
    const telemetryVariables = {
      correlationId: correlationIDSessionStorage,
      timestamp: new Date().toISOString(),
      searchQuery: searchTerm ?? '',
      searchLocation: {
        locationInputField: name ?? '',
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        appliedSearchRadiusMiles: searchRadius ?? 0,
      },
      interpretedAsNameSearch: searchType !== 'speciality',
      viewType: 'other',
      results: filteredResults,
      pageSize: providerList?.length,
      resultedInNullSearch: filteredResults?.length === 0,
      searchError: searchParams?.get('code') || '',
      searchPlatform: 'web',
      event: 'detail',
    };
    await providerSearchTelemetry({
      variables: telemetryVariables,
    });
  };

  useEffect(() => {
    if (
      searchParams?.get('source') === 'ProviderDetails' ||
      searchParams?.get('source') === 'FacilityDetails' ||
      searchParams?.get('source') === 'GroupDetails'
    ) {
      if (enableAutoCompleteTelemetry) {
        hitProviderSearchTelemetry();
      }
    }
  }, []);

  return (
    <React.Fragment>
      <PSXHeader
        breadcrumbs={breadcrumbs}
        dataTestId="error-4xx-search-form"
        showChips={false}
        showChoosePCPHeader={false}
        showMemberSelection={false}
      />
      <PageContainer>
        {isGehaTheme ? (
          <IconSymbol
            color="#C40000"
            css={{ marginTop: '111px' }}
            icon="report"
            size={60}
          />
        ) : (
          <img alt="whistle" src={WHISTLE_PNG} />
        )}
        <PageContent>
          <Text
            color="#C40000"
            css={{
              'abyss-text-root': {
                marginTop: isGehaTheme ? '14px' : '46px',
                marginBottom: '4px',
                '@media (max-width: 464px)': {
                  marginBottom: '29px',
                  marginTop: 'unset',
                },
                display: 'block',
              },
            }}
            fontSize="xs"
            fontWeight="bold"
          >
            {t('ERROR_PAGE.4xx.type')}
          </Text>
          <Heading
            css={{
              marginBottom: '4px',
              '@media (max-width: 464px)': {
                marginBottom: '16px',
              },
            }}
            level={1}
            size="lg"
          >
            {t('ERROR_PAGE.4xx.heading')}
          </Heading>
          <Heading
            css={{
              marginBottom: '12px',
              '@media (max-width: 464px)': {
                marginBottom: '20px',
                maxWidth: '181px',
              },
            }}
            level={6}
            size="xs"
          >
            {t('ERROR_PAGE.4xx.subHeading')}
          </Heading>
          {!(suppressPsxLinks && !currentMember) && (
            <Text fontSize="sm">{t('ERROR_PAGE.4xx.body1')}</Text>
          )}
        </PageContent>
      </PageContainer>

      {!isGuest && <StillNeedHelp />}
    </React.Fragment>
  );
};
